/**
 * @file phoneValidation.js
 * @description Sistema de validação de números de celular brasileiros para verificação de WhatsApp
 * Adaptado para usar validação nativa sem dependências externas
 */

// Lista completa dos DDDs válidos do Brasil (ANATEL 2024)
const validBrazilianDDDs = [
  '11', '12', '13', '14', '15', '16', '17', '18', '19', // São Paulo
  '21', '22', '24',                                     // Rio de Janeiro
  '27', '28',                                           // Espírito Santo
  '31', '32', '33', '34', '35', '37', '38',            // Minas Gerais
  '41', '42', '43', '44', '45', '46',                  // Paraná
  '47', '48', '49',                                     // Santa Catarina
  '51', '53', '54', '55',                              // Rio Grande do Sul
  '61',                                                 // Distrito Federal/Goiás
  '62', '64',                                           // Goiás/Tocantins
  '63',                                                 // Tocantins
  '65', '66',                                           // Mato Grosso
  '67',                                                 // Mato Grosso do Sul
  '68',                                                 // Acre
  '69',                                                 // Rondônia
  '71', '73', '74', '75', '77',                        // Bahia
  '79',                                                 // Sergipe
  '81', '87',                                           // Pernambuco
  '82',                                                 // Alagoas
  '83',                                                 // Paraíba
  '84',                                                 // Rio Grande do Norte
  '85', '88',                                           // Ceará
  '86', '89',                                           // Piauí
  '91', '93', '94',                                     // Pará
  '92', '97',                                           // Amazonas
  '95',                                                 // Roraima
  '96',                                                 // Amapá
  '98', '99'                                            // Maranhão
];

// String dos DDDs para uso em regex
const DDDs = validBrazilianDDDs.join('|');

/**
 * Formatos aceitos
 * ─────────────────────────────────────────────────────────────────────────
 *  • 79998126013        →  DD + 9 + número (11 dígitos)      ← celular sem +55
 *  • 7998126013         →  DD + número        (10 dígitos)   ← "antigo" sem +55
 *  • 5579998126013      → +55 DD + 9 + número (13 dígitos)   ← celular completo
 *  • 557998126013       → +55 DD + número    (12 dígitos)    ← "antigo" completo
 *
 *  Regras
 *  ─────────────────────────────────────────────────────────────────────────
 *  1. O "+55" é opcional (escrevemos como 55 porque o '+' não vem nos dados brutos).
 *  2. O DDD (dois dígitos) **tem** de existir e ser oficialmente atribuído pela ANATEL.
 *  3. Depois do DDD pode ou não existir o 9 extra dos celulares.
 *  4. Se tiver o 9, vêm mais 8 dígitos; se não tiver, vêm 8 dígitos.
 *  5. Qualquer coisa fora disso é inválida.
 */

const phoneRegex = new RegExp(
  `^(?:55)?` +          // (1) opcional 55
  `(?:${DDDs})` +          // (2) DDD autorizado
  `(?:9\\d{8}|\\d{8})$`        // (3) 9+8 díg ou só 8 díg
);

/**
 * Função para diagnosticar por que um número é inválido
 * @param {string} number - Número a ser validado
 * @returns {string} Motivo da invalidação
 */
const whyInvalid = number => {
  if (/\D/.test(number)) return 'contém caractere não numérico';
  if (!/^(?:55)?\d{2}/.test(number)) return 'faltando DDD';
  if (!new RegExp(`^(?:55)?(?:${DDDs})`).test(number))
    return 'DDD inexistente';
  const sem55 = number.replace(/^55/, '');
  if (![10, 11].includes(sem55.length))
    return 'quantidade de dígitos fora dos formatos 10 ou 11';
  if (!/^\d{10,11}$/.test(sem55))    // sobra só dígitos?
    return 'padrão de dígitos incorreto';
  return 'formato desconhecido';
};

/**
 * Valida uma lista de números de telefone
 * @param {Array<string>} list - Lista de números para validar
 * @returns {Object} Resultado da validação
 */
const validateList = list => {
  const invalid = [];
  for (const n of list) {
    if (!phoneRegex.test(n)) invalid.push({ n, reason: whyInvalid(n) });
  }
  return {
    total: list.length,
    invalidCount: invalid.length,
    invalid
  };
};

/**
 * Função para verificar se um DDD é válido
 * @param {string} ddd - DDD a ser verificado
 * @returns {boolean} True se o DDD é válido
 */
function isValidBrazilianDDD(ddd) {
  return validBrazilianDDDs.includes(ddd);
}

/**
 * Corrige número de telefone adicionando o 9 se necessário
 * @param {string} phone - Número de telefone
 * @returns {string|null} Número corrigido ou null se inválido
 */
function fixPhoneIfMissing9(phone) {
  const cleaned = phone.replace(/\D/g, '');

  // Cenário 1: Número já tem código do país +55
  if (cleaned.startsWith('55') && cleaned.length >= 12) {
    const ddd = cleaned.slice(2, 4);
    const number = cleaned.slice(4);

    // Verifica se o DDD é brasileiro válido
    if (!isValidBrazilianDDD(ddd)) {
      return null;
    }

    // Se tem 12 dígitos (55 + DDD + 8 dígitos), pode precisar do 9
    if (cleaned.length === 12) {
      const firstDigit = number[0];
      // Se começa com 6, 7, 8 ou 9, provavelmente é celular sem o 9
      if (['6', '7', '8', '9'].includes(firstDigit)) {
        return '+55' + ddd + '9' + number;
      }
    }

    // Se tem 13 dígitos (55 + DDD + 9 dígitos), já está correto
    return '+' + cleaned;
  }

  // Cenário 2: Número sem código do país
  if (cleaned.length >= 10 && cleaned.length <= 11) {
    const ddd = cleaned.slice(0, 2);
    const number = cleaned.slice(2);

    // Verifica se o DDD é brasileiro válido
    if (!isValidBrazilianDDD(ddd)) {
      return null;
    }

    // Se tem 10 dígitos (DDD + 8 dígitos), pode precisar do 9
    if (cleaned.length === 10) {
      const firstDigit = number[0];
      // Se começa com 6, 7, 8 ou 9, provavelmente é celular sem o 9
      if (['6', '7', '8', '9'].includes(firstDigit)) {
        return '+55' + ddd + '9' + number;
      }
    }

    // Se tem 11 dígitos (DDD + 9 dígitos), já está correto
    return '+55' + cleaned;
  }

  return null;
}

/**
 * Valida um número de telefone brasileiro
 * @param {string} phone - Número de telefone para validar
 * @returns {boolean} True se o telefone é válido
 */
export function isValidPhoneNumber(phone) {
  if (typeof phone !== 'string') return false;
  const cleanPhone = phone.trim();
  if (!cleanPhone) return false;

  // Remove todos os caracteres não numéricos
  const cleaned = cleanPhone.replace(/\D/g, '');

  // Usa a regex principal para validação
  return phoneRegex.test(cleaned);
}

/**
 * Verifica se um lead tem WhatsApp válido
 * @param {Object} lead - Objeto do lead
 * @returns {boolean} True se o lead tem WhatsApp válido
 */
export function hasValidWhatsApp(lead) {
  if (!lead) return false;

  const situacaoInterna = lead.situacaoInterna

  return !(situacaoInterna === "Não é WhatsApp")
}

/**
 * Adiciona informação de WhatsApp ao lead mantendo a situação original
 * @param {Object} lead - Objeto do lead
 * @returns {Object} Lead com informação de WhatsApp adicionada
 */
export function addWhatsAppInfo(lead) {
  const hasWhatsApp = hasValidWhatsApp(lead);
  
  return {
    ...lead,
    // Mantém a situação interna original
    situacaoInterna: lead.situacaoInterna,
    // Adiciona informação sobre WhatsApp
    temWhatsApp: hasWhatsApp,
    statusWhatsApp: hasWhatsApp ? 'Válido' : 'Inválido'
  };
}

/**
 * Processa um array de leads adicionando informação de WhatsApp
 * @param {Array<Object>} leads - Array de leads
 * @returns {Array<Object>} Array de leads com informação de WhatsApp
 */
export function processLeadsWithWhatsAppInfo(leads) {
  if (!Array.isArray(leads)) {
    return [];
  }

  return leads.map(lead => addWhatsAppInfo(lead));
}

/**
 * Filtra leads que têm WhatsApp válido
 * @param {Array<Object>} leads - Array de leads
 * @returns {Array<Object>} Array de leads com WhatsApp válido
 */
export function getLeadsWithValidWhatsApp(leads) {
  if (!Array.isArray(leads)) {
    return [];
  }

  return leads.filter(lead => hasValidWhatsApp(lead));
}

/**
 * Filtra leads que NÃO têm WhatsApp válido
 * @param {Array<Object>} leads - Array de leads
 * @returns {Array<Object>} Array de leads sem WhatsApp válido
 */
export function getLeadsWithoutValidWhatsApp(leads) {
  if (!Array.isArray(leads)) {
    return [];
  }

  return leads.filter(lead => !hasValidWhatsApp(lead));
}

/**
 * Agrupa leads por situação interna e status do WhatsApp
 * @param {Array<Object>} leads - Array de leads
 * @returns {Object} Leads agrupados por situação e WhatsApp
 */
export function groupLeadsBySituationAndWhatsApp(leads) {
  if (!Array.isArray(leads)) {
    return {};
  }

  const grouped = {};

  leads.forEach(lead => {
    const situacao = lead.situacaoInterna || 'Sem Situação';
    const hasWhatsApp = hasValidWhatsApp(lead);
    
    if (!grouped[situacao]) {
      grouped[situacao] = {
        total: 0,
        comWhatsApp: 0,
        semWhatsApp: 0,
        leads: []
      };
    }

    grouped[situacao].total++;
    grouped[situacao].leads.push({
      ...lead,
      temWhatsApp: hasWhatsApp
    });

    if (hasWhatsApp) {
      grouped[situacao].comWhatsApp++;
    } else {
      grouped[situacao].semWhatsApp++;
    }
  });

  return grouped;
}

/**
 * Função para normalizar um número de telefone brasileiro
 * @param {string} phone - Número de telefone para normalizar
 * @returns {string} Número normalizado ou string vazia se inválido
 */
export function normalizePhoneNumber(phone) {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  try {
    const correctedPhone = fixPhoneIfMissing9(phone);
    if (!correctedPhone) {
      return '';
    }

    // Remove o + se existir e valida
    const cleaned = correctedPhone.replace(/^\+/, '');
    if (phoneRegex.test(cleaned)) {
      // Retorna no formato E.164 (ex: +5511999999999)
      return '+' + cleaned;
    }

    return '';
  } catch (error) {
    return '';
  }
}

/**
 * Função para formatar um número de telefone brasileiro
 * @param {string} phone - Número de telefone para formatar
 * @param {boolean} includeCountryCode - Se deve incluir o código do país
 * @returns {string} Número formatado ou string vazia se inválido
 */
export function formatPhoneNumber(phone, includeCountryCode = true) {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  const normalized = normalizePhoneNumber(phone);
  if (!normalized) {
    return '';
  }

  // Remove o + para processar
  const cleaned = normalized.replace(/^\+/, '');

  // Extrai as partes do número
  let ddd = '';
  let number = '';

  if (cleaned.startsWith('55')) {
    ddd = cleaned.slice(2, 4);
    number = cleaned.slice(4);
  } else {
    ddd = cleaned.slice(0, 2);
    number = cleaned.slice(2);
  }

  // Formata o número
  if (number.length === 9) {
    // Celular: 9 XXXX-XXXX
    number = `${number[0]} ${number.slice(1, 5)}-${number.slice(5)}`;
  } else if (number.length === 8) {
    // Fixo: XXXX-XXXX
    number = `${number.slice(0, 4)}-${number.slice(4)}`;
  }

  if (includeCountryCode) {
    const countryCode = '55';
    return `+${countryCode} (${ddd}) ${number}`;
  } else {
    return `(${ddd}) ${number}`;
  }
}

/**
 * Função para obter informações detalhadas de um número de telefone
 * @param {string} phone - Número de telefone
 * @returns {Object|null} Informações detalhadas do telefone ou null se inválido
 */
export function getPhoneInfo(phone) {
  if (!phone || typeof phone !== 'string') {
    return null;
  }

  const normalized = normalizePhoneNumber(phone);
  if (!normalized) {
    return null;
  }

  const cleaned = normalized.replace(/^\+/, '');

  let ddd = '';
  let number = '';

  if (cleaned.startsWith('55')) {
    ddd = cleaned.slice(2, 4);
    number = cleaned.slice(4);
  } else {
    ddd = cleaned.slice(0, 2);
    number = cleaned.slice(2);
  }

  const type = number.length === 9 ? 'MOBILE' : 'FIXED_LINE';

  return {
    isValid: true,
    country: 'BR',
    countryCallingCode: '55',
    nationalNumber: ddd + number,
    number: normalized,
    type: type,
    isPossible: true,
    formatE164: normalized,
    formatInternational: formatPhoneNumber(phone, true),
    formatNational: formatPhoneNumber(phone, false),
    formatURI: `tel:${normalized}`,
    ddd: ddd
  };
}

/**
 * Função para obter estatísticas de WhatsApp dos leads
 * @param {Array<Object>} leads - Array de leads
 * @returns {Object} Estatísticas de WhatsApp
 */
export function getWhatsAppStats(leads) {
  if (!Array.isArray(leads)) {
    return {
      total: 0,
      comWhatsApp: 0,
      semWhatsApp: 0,
      percentualComWhatsApp: 0,
      percentualSemWhatsApp: 0,
      mobileCount: 0,
      fixedLineCount: 0,
      otherTypesCount: 0
    };
  }

  const total = leads.length;
  const leadsComWhatsApp = leads.filter(lead => hasValidWhatsApp(lead));
  const comWhatsApp = leadsComWhatsApp.length;
  const semWhatsApp = total - comWhatsApp;

  // Conta tipos de telefone para números válidos
  let mobileCount = 0;
  let fixedLineCount = 0;
  let otherTypesCount = 0;

  leadsComWhatsApp.forEach(lead => {
    const phoneInfo = getPhoneInfo(lead.whatsAppId || lead.telefone);
    if (phoneInfo) {
      switch (phoneInfo.type) {
        case 'MOBILE':
          mobileCount++;
          break;
        case 'FIXED_LINE':
          fixedLineCount++;
          break;
        default:
          otherTypesCount++;
          break;
      }
    }
  });

  return {
    total,
    comWhatsApp,
    semWhatsApp,
    percentualComWhatsApp: total > 0 ? (comWhatsApp / total) * 100 : 0,
    percentualSemWhatsApp: total > 0 ? (semWhatsApp / total) * 100 : 0,
    mobileCount,
    fixedLineCount,
    otherTypesCount,
    validationMethod: 'native-regex'
  };
}

/**
 * Função para validar uma lista de números
 * @param {Array<string>} phoneList - Lista de números para validar
 * @returns {Object} Resultado da validação
 */
export function validatePhoneList(phoneList) {
  return validateList(phoneList);
}

/**
 * Função para obter o motivo de invalidação de um número
 * @param {string} phone - Número de telefone
 * @returns {string} Motivo da invalidação
 */
export function getInvalidReason(phone) {
  if (!phone || typeof phone !== 'string') {
    return 'número vazio ou inválido';
  }

  const cleaned = phone.replace(/\D/g, '');
  return whyInvalid(cleaned);
}